import { getSeoUrl } from 'utils/links';
import {
  CATEGORY_URL,
  CVA_DEFAULT_LOCALE,
  CVA_LEARN_MORE_URL,
  CVA_SPECIFIC_SUPPORTED_LANGUAGES,
  OMM_BOOK_META
} from './[part-name]/[part-number]/constants/constants';
import {
  IS_CUSTOMER_SUPPORT_ENABLE,
  PCC_HEADLESS_ASSET_LANDING_PAGE,
  PCC_HEADLESS_BLOCKS_CATEGORY_PAGE,
  PCC_HEADLESS_BLOCKS_PDP_TOP_SECTION,
  PCC_PLP_VIEW_SPECS_DRAWER,
  PCC_SEARCH_RESULTS_SPECIFICATIONS_DRAWER,
  PCC_UPDATE_PDPCTA_TO_SEA_PARTS
} from 'constants/featureFlag';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { CONTENT_MIME_TYPE, CONTENT_NAME } from 'app/api/omm/constant';

export const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
    window.scrollBy({ top: -250, behavior: 'smooth' });
  }
};

export const getFilteredCategories = (categories, alpNonRelatedCategories) => {
  return categories.filter(
    (part) => !alpNonRelatedCategories.includes(part.identifier)
  );
};

export const getCategoryPageUrl = (
  url: string,
  isHeadlessCategoryPage: boolean,
  params
) => {
  const categoryUrl = isHeadlessCategoryPage
    ? `/${CATEGORY_URL}/${url}`
    : `/${url}`;
  return getSeoUrl(params, categoryUrl);
};

export const getMaintenceRepairList = (
  pmkitData,
  SSROData,
  MAINTENANCE_REPAIR_LIMIT = 5
) => {
  let combinedKits = pmkitData.filter((item) => item.isCatKit);
  if (combinedKits.length < MAINTENANCE_REPAIR_LIMIT) {
    combinedKits = [...combinedKits, ...SSROData];
  }
  return combinedKits.length > MAINTENANCE_REPAIR_LIMIT
    ? combinedKits.slice(0, MAINTENANCE_REPAIR_LIMIT)
    : combinedKits;
};

export const getPMKitsData = (pmKitsList) => {
  const catKitList = [];
  const dealerKitList = [];
  for (const pmKitsItem of pmKitsList) {
    if (pmKitsItem.isCatKit) {
      catKitList.push(pmKitsItem);
    } else {
      dealerKitList.push(pmKitsItem);
    }
  }
  return { catKitList, dealerKitList };
};

export const getFeatureFlag = (storeFeatures) => {
  return {
    isPCCAssetLandingPageFlagEnabled:
      storeFeatures?.[PCC_HEADLESS_ASSET_LANDING_PAGE] ?? false,
    isPCCCategoryHeadlessPageEnabled:
      storeFeatures?.[PCC_HEADLESS_BLOCKS_CATEGORY_PAGE] ?? false,
    isHeadlessPDPEnabled:
      storeFeatures?.[PCC_HEADLESS_BLOCKS_PDP_TOP_SECTION] ?? false,
    isPCCUpdatePDPCTAToSeaPartsEnabled:
      storeFeatures?.[PCC_UPDATE_PDPCTA_TO_SEA_PARTS] ?? false,
    pccSearchResultsSpecificationsDrawer:
      storeFeatures?.[PCC_SEARCH_RESULTS_SPECIFICATIONS_DRAWER] ?? false,
    pccPLPViewSpecsDrawer: storeFeatures?.[PCC_PLP_VIEW_SPECS_DRAWER] ?? false,
    isCustomerSupportEnabled:
      storeFeatures?.[IS_CUSTOMER_SUPPORT_ENABLE] ?? false
  };
};

export const getIsCSPAndIAValidUser = (user) => {
  return (
    !user.userAccessFlags.isBuyOnBehalf &&
    !user.userAccessFlags.isDealerUser &&
    !isEmpty(user.ccidProd)
  );
};

export const getUserDetails = (userData) => {
  const {
    ccidProd,
    userAccessFlags: {
      isLoggedIn,
      isCspUser,
      isBuyOnBehalf,
      isDealerUser,
      isInstantAccessUser
    }
  } = userData;

  return {
    isLoggedIn,
    isCspUser,
    isBuyOnBehalf,
    isDealerUser,
    ccidProd,
    isInstantAccessUser
  };
};

export const cvaGetLink = (locale: string): string => {
  if (CVA_SPECIFIC_SUPPORTED_LANGUAGES.includes(locale)) {
    return replaceTokensInString(CVA_LEARN_MORE_URL, locale);
  } else {
    return replaceTokensInString(CVA_LEARN_MORE_URL, CVA_DEFAULT_LOCALE);
  }
};

export const getOmmImageUrl = (metaData) => {
  const imageMeta = metaData.find(
    (meta) => meta.name === OMM_BOOK_META.sampleImageUrl
  );
  if (imageMeta) {
    const matches = OMM_BOOK_META.pattern.exec(imageMeta.value);
    return matches ? matches[0] : null;
  }
  return null;
};

export const getFilteredCatalogs = (contentLineItems) => {
  return contentLineItems.filter(
    ({ contentMimeType, contentName }) =>
      contentMimeType === CONTENT_MIME_TYPE && contentName === CONTENT_NAME
  );
};

export const getOMMCatalogs = (contentLineItems) => {
  const filteredCatalogs = getFilteredCatalogs(contentLineItems);
  return filteredCatalogs.map((omm) => {
    return {
      title: omm.shortDescription,
      uniqueId: omm.uri,
      imageUrl: getOmmImageUrl(omm.metadata)
    };
  });
};
